/**
 * DisruptionCategoryDetailPage - Detailansicht/Neuanlage einer Störungskategorie
 * 
 * Sektionen:
 * - Grunddaten (Name)
 * - Technisch (Code - read-only, auto-generiert)
 * - Gefahrenzone (Deaktivieren)
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, RefreshCw, Save, Trash2, Copy, ChevronDown, ChevronRight } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';

interface DisruptionCategory {
  id: string;
  code: string;
  name: string;
  color: string | null;
  isActive: boolean;
}

interface FormData {
  name: string;
}

const INITIAL_FORM: FormData = {
  name: '',
};

export default function DisruptionCategoryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';
  
  const [category, setCategory] = useState<DisruptionCategory | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [techOpen, setTechOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadCategory = async () => {
    if (isNew || !id) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/disruption-categories/${id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCategory(data);
        setFormData({
          name: data.name,
        });
      } else if (res.status === 404) {
        navigate('/admin/disruption-categories');
      }
    } catch (err) {
      console.error('Fehler beim Laden der Störungskategorie:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!isNew) {
      loadCategory();
    }
  }, [id, isNew]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Name ist erforderlich');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const url = isNew ? '/api/admin/disruption-categories' : `/api/admin/disruption-categories/${id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: formData.name.trim(),
        }),
      });

      if (res.ok) {
        const data = await res.json();
        if (isNew) {
          navigate(`/admin/disruption-categories/${data.id}`);
        } else {
          loadCategory();
        }
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || data.message || 'Fehler beim Speichern');
      }
    } catch (err) {
      setError('Netzwerkfehler');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!id || isNew) return;
    
    setDeleting(true);
    try {
      const res = await fetch(`/api/admin/disruption-categories/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (res.ok) {
        navigate('/admin/disruption-categories');
      }
    } catch (err) {
      console.error('Fehler beim Deaktivieren:', err);
    }
    setDeleting(false);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Laden...</div>
      </AdminLayout>
    );
  }

  if (!isNew && !category) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Störungskategorie nicht gefunden</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <Link 
              to="/admin/disruption-categories" 
              className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück zur Übersicht
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6" />
              {isNew ? 'Neue Störungskategorie' : category?.name}
              {!isNew && category && (
                <span className="text-sm font-mono text-slate-400">({category.code})</span>
              )}
            </h1>
            {!isNew && category && !category.isActive && (
              <p className="text-sm text-red-500 mt-1">(inaktiv)</p>
            )}
          </div>
          <div className="flex gap-2">
            {!isNew && (
              <button
                onClick={() => loadCategory()}
                disabled={loading}
                className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                title="Aktualisieren"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving || !formData.name.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Form Sections */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Grunddaten */}
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <h2 className="font-semibold text-slate-900 border-b pb-2">Grunddaten</h2>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="z.B. Maschinenfehler"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
            
            <div className="bg-slate-50 p-3 rounded text-sm text-slate-600">
              Diese Kategorie wird in der Störungserfassung zur Auswahl angezeigt.
            </div>
          </section>

          {/* Technisch (collapsed) */}
          {!isNew && category && (
            <section className="bg-white rounded-lg border overflow-hidden">
              <button
                onClick={() => setTechOpen(!techOpen)}
                className="w-full px-6 py-4 flex justify-between items-center hover:bg-slate-50"
              >
                <h2 className="font-semibold text-slate-900">Technische Details</h2>
                {techOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              
              {techOpen && (
                <div className="px-6 pb-6 space-y-4 border-t">
                  <div className="pt-4">
                    <label className="block text-sm font-medium text-slate-500 mb-1">Code (automatisch)</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={category.code}
                        readOnly
                        className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-mono text-sm text-slate-600"
                      />
                      <button
                        onClick={() => copyToClipboard(category.code)}
                        className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50"
                        title="Kopieren"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      Stabiler technischer Schlüssel (für Integrationen)
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-500 mb-1">ID</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={category.id}
                        readOnly
                        className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-mono text-xs text-slate-400"
                      />
                      <button
                        onClick={() => copyToClipboard(category.id)}
                        className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50"
                        title="Kopieren"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>

        {/* Danger Zone */}
        {!isNew && category?.isActive && (
          <section className="bg-white rounded-lg border border-red-200 p-6 space-y-4">
            <h2 className="font-semibold text-red-700 flex items-center gap-2">
              <Trash2 className="w-4 h-4" />
              Gefahrenzone
            </h2>
            
            {!deleteConfirmOpen ? (
              <button
                onClick={() => setDeleteConfirmOpen(true)}
                className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50"
              >
                Kategorie deaktivieren
              </button>
            ) : (
              <div className="p-4 bg-red-50 rounded-lg space-y-3">
                <p className="text-sm text-red-700">
                  Soll diese Kategorie wirklich deaktiviert werden? Sie wird dann nicht mehr zur Auswahl angezeigt.
                  Bestehende Störungen behalten ihre Kategorie (Snapshot).
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    {deleting ? 'Deaktivieren...' : 'Ja, deaktivieren'}
                  </button>
                  <button
                    onClick={() => setDeleteConfirmOpen(false)}
                    className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    </AdminLayout>
  );
}
